import { createRouter, createWebHistory } from 'vue-router'
// Ленивая загрузка страниц — каждая в отдельном чанке
const DashboardPage = () => import('../pages/DashboardPage.vue')
const DevicesPage = () => import('../pages/DevicesPage.vue')
const GeozonesPage = () => import('../pages/GeozonesPage.vue')
const MaintenancePage = () => import('../pages/MaintenancePage.vue')
const DriversPage = () => import('../pages/DriversPage.vue')
const TrailersPage = () => import('../pages/TrailersPage.vue')
const RegTechPage = () => import('../pages/RegTechPage.vue')
const SubUsersPage = () => import('../pages/SubUsersPage.vue')
const SharingPage = () => import('../pages/SharingPage.vue')
const PublicSharePage = () => import('../pages/PublicSharePage.vue')
const TrackerPairPage = () => import('../pages/TrackerPairPage.vue')
const ReportsPage = () => import('../pages/ReportsPage.vue')
const BillingPage = () => import('../pages/BillingPage.vue')
const NotificationsPage = () => import('../pages/NotificationsPage.vue')
const RequestsPage = () => import('../pages/RequestsPage.vue')
const SupportChatPage = () => import('../pages/SupportChatPage.vue')
const LoginPage = () => import('../pages/LoginPage.vue')
const DemoLoginPage = () => import('../pages/DemoLoginPage.vue')
const BlockedPage = () => import('../pages/BlockedPage.vue')
const HealthPage = () => import('../pages/HealthPage.vue')
const MigrationWelcomePage = () => import('../pages/MigrationWelcomePage.vue')
const WhatsNewPage = () => import('../pages/WhatsNewPage.vue')
import { clearToken, performLogout, getValidToken, isDealerUser, isClientBlocked, isImpersonated, isSubUser, hasPermission, isModuleEnabled } from '../services/authService'
import { getLanguage, t, isI18nReady } from '../services/i18nService'

// Домашня сторінка після входу: субкористувач і клієнт з вимкненим дашбордом → транспорт.
// Транспорт (/devices) — завжди доступний fallback, його модуль не вимикається.
// Виняток — виконавець завдань без доступу до транспорту: його дім — «Мої завдання».
function getHomeRoute() {
  if (isSubUser()) {
    if (hasPermission('canExecuteTasks') && !hasPermission('canViewDevices')) {
      return { name: 'my-tasks' }
    }
    return { name: 'devices' }
  }
  if (!isModuleEnabled('dashboard')) return { name: 'devices' }
  return { name: 'dashboard' }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: () => getHomeRoute(),
    },
    {
      path: '/login',
      name: 'login',
      component: LoginPage,
      // layout: system — отдельный макет для auth/system-страниц.
      meta: { titleKey: 'login.title', layout: 'system' },
    },
    // Прямая ссылка для входа в demo-аккаунт: /demo, /demo/en, /demo/ua
    {
      path: '/demo/:lang?',
      name: 'demo',
      component: DemoLoginPage,
      meta: { requiresAuth: false, layout: 'system' },
    },
    // Health check endpoint - доступен без авторизации для систем мониторинга
    {
      path: '/system/health',
      name: 'health',
      component: HealthPage,
      meta: { requiresAuth: false, layout: 'system', titleKey: 'health.title' },
    },
    // Сторінка активації акаунта для клієнтів, мігрованих з Hydra
    {
      path: '/welcome',
      name: 'migration-welcome',
      component: MigrationWelcomePage,
      meta: { requiresAuth: false, layout: 'system' },
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: DashboardPage,
      meta: { requiresAuth: true, titleKey: 'nav.dashboard', requiredModule: 'dashboard', layout: 'dashboard' },
    },
    {
      path: '/devices',
      name: 'devices',
      component: DevicesPage,
      meta: { requiresAuth: true, titleKey: 'nav.devices', requiredPermission: 'canViewDevices', layout: 'split' },
    },
    {
      path: '/geozones',
      name: 'geozones',
      component: GeozonesPage,
      meta: { requiresAuth: true, titleKey: 'nav.geozones', requiredPermission: 'canViewGeozones', requiredModule: 'geozones', layout: 'split' },
    },
    // Сервис заданий: диспетчерская страница с картой-редактором маршрута
    {
      path: '/tasks',
      name: 'tasks',
      component: () => import('../pages/TasksPage.vue'),
      meta: { requiresAuth: true, titleKey: 'nav.tasks', requiredPermission: 'canViewTasks', requiredModule: 'tasks', layout: 'split' },
    },
    // Кабинет исполнителя: только свои задания, mobile-first
    {
      path: '/my-tasks',
      name: 'my-tasks',
      component: () => import('../pages/MyTasksPage.vue'),
      meta: { requiresAuth: true, titleKey: 'nav.myTasks', requiredPermission: 'canExecuteTasks', requiredModule: 'tasks', layout: 'data' },
    },
    {
      path: '/maintenance',
      name: 'maintenance',
      component: MaintenancePage,
      meta: { requiresAuth: true, titleKey: 'nav.maintenance', requiredPermission: 'canViewMaintenance', requiredModule: 'maintenance', layout: 'split' },
    },
    {
      path: '/drivers',
      name: 'drivers',
      component: DriversPage,
      meta: { requiresAuth: true, titleKey: 'nav.drivers', requiredPermission: 'canViewDrivers', layout: 'data' },
    },
    {
      path: '/personnel',
      name: 'personnel',
      component: () => import('../pages/PersonnelPage.vue'),
      meta: { requiresAuth: true, titleKey: 'nav.personnel', requiredPermission: 'canViewPersonnel', requiredModule: 'personnel', layout: 'data' },
    },
    // Тахограф: єдина вітрина вивантажень файлів (DDD) по машинах і водіях,
    // джерело/виробник обладнання свідомо не показуємо — це деталь бекенда.
    {
      path: '/tacho',
      name: 'tacho',
      component: () => import('../pages/TachoPage.vue'),
      meta: { requiresAuth: true, titleKey: 'nav.tacho', requiredModule: 'tacho', layout: 'data' },
    },
    {
      path: '/trailers',
      name: 'trailers',
      component: TrailersPage,
      meta: { requiresAuth: true, titleKey: 'nav.trailers', requiredPermission: 'canViewTrailers', layout: 'data' },
    },
    // АЗС (платний модуль stationcards): огляд заправок, журнал із приходами, картки TERES і доступ до колонок
    {
      path: '/fuel-stations',
      name: 'fuel-stations',
      component: () => import('../pages/FuelStationsPage.vue'),
      meta: { requiresAuth: true, titleKey: 'nav.fuelStations', requiredPermission: 'canSeeStation', requiredModule: 'stationcards', layout: 'data' },
    },
    // Стара адреса «Картки АЗС» у закладках
    {
      path: '/station-cards',
      redirect: { path: '/fuel-stations', query: { tab: 'cards' } },
    },
    // Генератори (платний модуль generators): стан лічильників по фазах, виробіток,
    // моточаси, розхід пального і л/кВт·год
    {
      path: '/generators',
      name: 'generators',
      component: () => import('../pages/GeneratorsPage.vue'),
      meta: { requiresAuth: true, titleKey: 'nav.generators', requiredPermission: 'canSeeGenerators', requiredModule: 'generators', layout: 'data' },
    },
    {
      path: '/regtech',
      name: 'regtech',
      component: RegTechPage,
      meta: { requiresAuth: true, titleKey: 'nav.regtech', requiredPermission: 'canViewRegtech', layout: 'data' },
    },
    {
      path: '/sub-users',
      name: 'sub-users',
      component: SubUsersPage,
      meta: { requiresAuth: true, titleKey: 'nav.subUsers', hideForSubUser: true, requiredModule: 'subusers', layout: 'data' },
    },
    {
      path: '/sharing',
      name: 'sharing',
      component: SharingPage,
      meta: { requiresAuth: true, titleKey: 'nav.sharing', hideForSubUser: true, requiredModule: 'sharing', layout: 'data' },
    },
    // Ссылка подключения телефона (cloudGPS Tracker) из админки: открыта на телефоне
    // без приложения. Без auth и без chrome портала: её открывает сотрудник, а не клиент
    {
      path: '/tracker/pair',
      name: 'tracker-pair',
      component: TrackerPairPage,
      meta: { requiresAuth: false, layout: 'system', publicPage: true },
    },
    // Публичный просмотрщик ссылки шаринга. Без auth, без chrome портала.
    {
      path: '/share/:token',
      name: 'public-share',
      component: PublicSharePage,
      meta: { requiresAuth: false, layout: 'system', publicPage: true },
    },
    {
      path: '/reports',
      name: 'reports',
      component: ReportsPage,
      meta: { requiresAuth: true, titleKey: 'nav.reports', requiredPermission: 'canRequestReports', requiredModule: 'reports', layout: 'split' },
    },
    {
      path: '/billing',
      name: 'billing',
      component: BillingPage,
      meta: { requiresAuth: true, titleKey: 'nav.billing', requiredPermission: 'canViewBilling', layout: 'data' },
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: NotificationsPage,
      meta: { requiresAuth: true, titleKey: 'nav.notifications', requiredPermission: 'canViewNotifications', layout: 'data' },
    },
    {
      path: '/requests',
      name: 'requests',
      component: RequestsPage,
      // Заявки дилеру: власник завжди, співробітник — з правом canManageRequests
      meta: { requiresAuth: true, titleKey: 'nav.requests', layout: 'data', requiredPermission: 'canManageRequests' },
    },
    {
      path: '/requests/support/:id',
      name: 'support-chat',
      component: SupportChatPage,
      meta: { requiresAuth: true, titleKey: 'support.chat.defaultSubject', layout: 'split', hideForSubUser: true },
    },
    {
      // Стрічка «Що нового»: доступна всім, у т.ч. субкористувачам (лише читання)
      path: '/news',
      name: 'whats-new',
      component: WhatsNewPage,
      meta: { requiresAuth: true, titleKey: 'news.title', layout: 'data' },
    },
    {
      path: '/blocked',
      name: 'blocked',
      component: BlockedPage,
      meta: { requiresAuth: true, allowBlocked: true, titleKey: 'blocked.title', layout: 'system' },
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  // Перехід на /login з ?login= (з листа або форми встановлення пароля) — це намір увійти під
  // конкретним акаунтом. Якщо у браузері жива сесія (можливо, іншого клієнта) — повністю
  // розлогінюємось, інакше guard нижче перекине на дашборд старого акаунта замість форми входу.
  if (to.name === 'login' && to.query.login && getValidToken()) {
    await performLogout()
  }

  const token = getValidToken()
  const hasDealerRole = isDealerUser()
  // Impersonate («Увійти як клієнт» з адмінки) не замикаємо на /blocked: адміну портал
  // заблокованого клієнта потрібен для розбору. Сам клієнт як і раніше бачить лише /blocked.
  const blocked = isClientBlocked() && !isImpersonated()

  if (to.meta.requiresAuth) {
    if (!token) {
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }

    if (!hasDealerRole) {
      clearToken()
      next({ name: 'login', query: { redirect: to.fullPath, reason: 'insufficient_role' } })
      return
    }

    // Заблокированный клиент видит только страницу блокировки
    if (blocked && !to.meta.allowBlocked) {
      next({ name: 'blocked' })
      return
    }

    // Розділи заборонені для субкористувачів
    if (isSubUser() && to.meta.hideForSubUser) {
      next({ name: 'devices' })
      return
    }
    // Вимкнений модуль порталу (dashboard, reports, geozones, maintenance, subusers, sharing) → на домашню
    if (to.meta.requiredModule && !isModuleEnabled(to.meta.requiredModule)) {
      next(getHomeRoute())
      return
    }
    // Перевірка дозволу для всіх користувачів (відключені модулі + права субкористувачів)
    if (to.meta.requiredPermission && !hasPermission(to.meta.requiredPermission)) {
      next(getHomeRoute())
      return
    }
  }

  // Если клиент не заблокирован, не пускаем на страницу блокировки
  if (to.name === 'blocked' && !blocked) {
    next(getHomeRoute())
    return
  }

  if (to.name === 'login' && token && hasDealerRole) {
    next(getHomeRoute())
    return
  }

  next()
})

router.afterEach((to) => {
  // Синхронизируем html lang, чтобы браузер корректно воспринимал текущий язык.
  document.documentElement.lang = getLanguage()

  // Публичные страницы (например, /share/{token}) — белые ярлыки.
  // Не подставляем сюда «СКТ Глобус» — у нас мультибрендинг и ссылку
  // может смотреть клиент другого дилера. Сам компонент PublicSharePage
  // ставит document.title = метке ссылки, когда она загрузится.
  if (to.meta?.publicPage) return

  // Динамический title страницы: "Назва сторінки — СКТ «Глобус»"
  // Пропускаємо якщо i18n ще не завантажено - title оновиться після завантаження
  if (!isI18nReady()) return

  const brandName = t('brand.name')
  if (to.meta?.titleKey) {
    const pageTitle = t(to.meta.titleKey)
    document.title = `${pageTitle} — ${brandName}`
  } else {
    document.title = brandName
  }
})

export default router
